import { insertQuoteRequest } from "@/lib/lead-insert";
import { getSubmissionSource } from "@/lib/submission-source";
import { openMailtoBackup } from "@/lib/open-mailto-backup";

export interface SubmitQuoteInput {
  formId: string;
  productId: string;
  productName: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string | null;
  shippingAddress?: string | null;
  sizeOption?: string | null;
  fabricOption?: string | null;
  colorOption?: string | null;
  quantity?: number;
  notes: string;
  /**
   * If true (default), also opens the visitor's email client with a pre-filled
   * message so they can press Send as a manual backup copy. The primary record
   * is always saved to the database first.
   */
  mailtoBackup?: boolean;
}

/**
 * Saves a quote request to the database. This automatically:
 *  - mirrors into customer_submissions
 *  - fires the notify_new_submission trigger
 *  - emails office@texasmadeshade.com with the "QUOTE REQUESTED" subject line
 *
 * On success, ALSO opens the visitor's own email client with a pre-filled
 * message addressed to office@texasmadeshade.com as a manual backup.
 */
export async function submitQuoteRequest(input: SubmitQuoteInput) {
  const source = getSubmissionSource(input.formId);
  const { id, error } = await insertQuoteRequest({
    product_id: input.productId,
    product_name: input.productName,
    customer_name: input.customerName,
    customer_email: input.customerEmail,
    customer_phone: input.customerPhone || null,
    shipping_address: input.shippingAddress || null,
    size_option: input.sizeOption || null,
    fabric_option: input.fabricOption || null,
    color_option: input.colorOption || null,
    quantity: input.quantity ?? 1,
    notes: input.notes,
    ...source,
  });


  if (!error && input.mailtoBackup !== false) {
    const lines = [
      `Name: ${input.customerName}`,
      `Email: ${input.customerEmail}`,
      input.customerPhone ? `Phone: ${input.customerPhone}` : null,
      input.shippingAddress ? `Address: ${input.shippingAddress}` : null,
      "",
      `Product: ${input.productName}`,
      input.sizeOption ? `Size: ${input.sizeOption}` : null,
      input.fabricOption ? `Fabric: ${input.fabricOption}` : null,
      input.colorOption ? `Color: ${input.colorOption}` : null,
      input.quantity && input.quantity !== 1 ? `Quantity: ${input.quantity}` : null,
      "",
      "Details:",
      input.notes || "(none)",
    ].filter((l): l is string => l !== null);

    openMailtoBackup({
      subject: `${input.productName} — ${input.customerName}`,
      body: lines.join("\n"),
    });
  }

  return { error, id };
}
